import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type PurchaseQuoteStatus = "ABIERTA" | "CONVERTIDA" | "ANULADA";

export const PURCHASE_QUOTE_STATUS_LABEL: Record<PurchaseQuoteStatus, string> = {
  ABIERTA: "Abierta",
  CONVERTIDA: "Convertida en orden de compra",
  ANULADA: "Anulada",
};

export interface PurchaseQuoteLineRecord {
  id: string;
  productId: string;
  quantity: string;
  estimatedUnitCost: string;
  subtotal: string;
  product?: { id: string; sku: string; name: string; unit: string };
}

export interface PurchaseQuoteRecord {
  id: string;
  branchId: string;
  branch?: { id: string; name: string };
  supplierId: string;
  supplier?: { id: string; name: string; taxId: string | null; phone: string | null; email: string | null };
  quoteNumber: number;
  quoteDate: string;
  validUntil: string | null;
  subtotal: string;
  taxTotal: string;
  total: string;
  status: PurchaseQuoteStatus;
  notes: string | null;
  isExpired: boolean;
  purchaseId: string | null;
  purchase?: { id: string; status: string; purchaseDate: string; total: string } | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
  lines: PurchaseQuoteLineRecord[];
}

export interface ListPurchaseQuotesFilters {
  page?: number;
  pageSize?: number;
  supplierId?: string;
  status?: PurchaseQuoteStatus;
  branchId?: string;
}

/** Cotizaciones de compra: precio estimado de un proveedor, ANTES de crear una orden de compra real — no tocan inventario ni cuentas por pagar (ver purchase-quote.service.ts en el backend). */
export function usePurchaseQuotes(filters: ListPurchaseQuotesFilters = {}) {
  return useQuery({
    queryKey: ["store", "purchase-quotes", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PurchaseQuoteRecord[]>>("/purchase-quotes", { params: filters });
      return data;
    },
  });
}

export function usePurchaseQuote(id: string | null) {
  return useQuery({
    queryKey: ["store", "purchase-quotes", "detail", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PurchaseQuoteRecord>>(`/purchase-quotes/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface CreatePurchaseQuotePayload {
  branchId: string;
  supplierId: string;
  validUntil?: string;
  notes?: string;
  lines: Array<{ productId: string; quantity: number | string; estimatedUnitCost: number | string }>;
}

export function useCreatePurchaseQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePurchaseQuotePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseQuoteRecord>>("/purchase-quotes", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "purchase-quotes"] }),
  });
}

export function useVoidPurchaseQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseQuoteRecord>>(`/purchase-quotes/${id}/void`, {});
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "purchase-quotes"] }),
  });
}

export interface ConvertPurchaseQuotePayload {
  id: string;
  purchaseDate: string;
  invoiceNumber?: string;
  notes?: string;
}

/** Convierte la cotización en una orden de compra real (BORRADOR, no toca inventario todavía) — ver purchase-quote.service.ts::convertPurchaseQuoteToPurchase. */
export function useConvertPurchaseQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: ConvertPurchaseQuotePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PurchaseQuoteRecord>>(`/purchase-quotes/${id}/convert`, body);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "purchase-quotes"] });
      queryClient.invalidateQueries({ queryKey: ["store", "purchases"] });
    },
  });
}
